import type { ReactNode } from 'react';
import { createContext, lazy, Suspense, useContext, useMemo } from 'react';
import type { UseListBuilderOptions } from '../../hooks/useListBuilder';
import { useListBuilder } from '../../hooks/useListBuilder';
import type { ListBuilderService } from '../../services/ListBuilderService';
import type {
	ColumnConfiguration,
	ListBuilderActions,
	ListBuilderState,
	ListConfiguration,
} from '../../types';

// Lazy load the modal to break the circular dependency
const ListBuilderModal = lazy(() =>
	import('./ListBuilderModal').then((module) => ({
		default: module.ListBuilderModal,
	})),
);

// Extended state type with derived properties (mirrors the derived state
// useListBuilder merges into the state it returns)
type ExtendedListBuilderState = ListBuilderState & {
	activeLists: Array<ListConfiguration>;
	hasUnsavedChanges: boolean;
	selectedColumn: ColumnConfiguration | undefined;
	canSave: boolean;
};

// Service context value - stable reference that rarely changes
interface ServiceContextValue {
	listBuilderService: ListBuilderService;
	listKey: string;
	additionalContext?: Record<string, any>;
}

// Split contexts for better performance
// StateContext - changes frequently, subscribers re-render on state changes
const StateContext = createContext<ExtendedListBuilderState | null>(null);

// ActionsContext - stable reference, rarely causes re-renders
const ActionsContext = createContext<ListBuilderActions | null>(null);

// ServiceContext - stable reference, never causes re-renders
const ServiceContext = createContext<ServiceContextValue | null>(null);

// Legacy combined context for backward compatibility
interface ListBuilderContextValue {
	state: ExtendedListBuilderState;
	actions: ListBuilderActions;
	listBuilderService: ListBuilderService;
	listKey: string;
	additionalContext?: Record<string, any>;
}

const ListBuilderContext = createContext<ListBuilderContextValue | null>(null);

export interface ListBuilderProviderProps extends UseListBuilderOptions {
	children: ReactNode;
	onConfigurationSaved?: () => void;
}

export function ListBuilderProvider({
	children,
	onConfigurationSaved,
	...options
}: ListBuilderProviderProps) {
	return (
		<ListBuilderProviderInner
			{...options}
			onConfigurationSaved={onConfigurationSaved}
		>
			{children}
		</ListBuilderProviderInner>
	);
}

// Inner component that manages list builder state
function ListBuilderProviderInner({
	children,
	onConfigurationSaved,
	...options
}: ListBuilderProviderProps) {
	const listBuilderValue = useListBuilder({
		...options,
		onConfigurationSaved,
	});

	const listKey = options.listKey ?? '';

	// Memoize service context value - this rarely changes
	const serviceValue = useMemo(
		() => ({
			listBuilderService: listBuilderValue.listBuilderService,
			listKey,
			additionalContext: options.additionalContext,
		}),
		[listBuilderValue.listBuilderService, listKey, options.additionalContext],
	);

	// Legacy combined context value for backward compatibility
	const contextValue = useMemo(
		() => ({
			...listBuilderValue,
			listKey,
			additionalContext: options.additionalContext,
		}),
		[listBuilderValue, listKey, options.additionalContext],
	);

	return (
		<ServiceContext.Provider value={serviceValue}>
			<ActionsContext.Provider value={listBuilderValue.actions}>
				<StateContext.Provider value={listBuilderValue.state}>
					<ListBuilderContext.Provider value={contextValue}>
						{children}
						<Suspense fallback={null}>
							<ListBuilderModal />
						</Suspense>
					</ListBuilderContext.Provider>
				</StateContext.Provider>
			</ActionsContext.Provider>
		</ServiceContext.Provider>
	);
}

// Legacy combined context hook - for backward compatibility
// Prefer using the split context hooks for better performance
export function useListBuilderContext(): ListBuilderContextValue {
	const context = useContext(ListBuilderContext);

	if (context == null) {
		throw new Error(
			'useListBuilderContext must be used within a ListBuilderProvider',
		);
	}

	return context;
}

// Safe version that returns null if not in a ListBuilder context
export function useListBuilderContextSafe(): ListBuilderContextValue | null {
	return useContext(ListBuilderContext);
}

// ============================================
// SPLIT CONTEXT HOOKS - Use these for better performance
// ============================================

/**
 * Hook to access only the state portion of the context.
 * Use this when you only need to read state values.
 * Re-renders when state changes.
 */
export function useListBuilderState(): ExtendedListBuilderState {
	const state = useContext(StateContext);

	if (state == null) {
		throw new Error(
			'useListBuilderState must be used within a ListBuilderProvider',
		);
	}

	return state;
}

/**
 * Hook to access only the actions portion of the context.
 * Use this when you only need to call actions (e.g., buttons, event handlers).
 * Does NOT re-render when state changes - only when actions reference changes (rarely).
 */
export function useListBuilderActions(): ListBuilderActions {
	const actions = useContext(ActionsContext);

	if (actions == null) {
		throw new Error(
			'useListBuilderActions must be used within a ListBuilderProvider',
		);
	}

	return actions;
}

/**
 * Hook to access service context (listBuilderService, listKey, additionalContext).
 * Use this when you need the service or listKey without subscribing to state changes.
 * Does NOT re-render when state changes.
 */
export function useListBuilderService(): ServiceContextValue {
	const service = useContext(ServiceContext);

	if (service == null) {
		throw new Error(
			'useListBuilderService must be used within a ListBuilderProvider',
		);
	}

	return service;
}

// ============================================
// CONVENIENCE HOOKS - Use specific state slices
// ============================================

export function useCurrentList() {
	const state = useListBuilderState();

	return state.currentList;
}

export function useActiveLists() {
	const state = useListBuilderState();

	return state.activeLists;
}

export function useSelectedColumn() {
	const state = useListBuilderState();

	return state.selectedColumn;
}
